import React, { type MouseEventHandler, useMemo } from 'react';
import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';
import { useFormikContext } from 'formik';
import { useApolloClient } from '@apollo/client/react';
import ActionButtons from 'molecules/ActionButtons';
import PayPalButton from 'molecules/PayPalButton';
import { CAMPAIGN_TYPES } from 'lib/graphql/queries/campaign';
import type { CampaignType } from 'types/campaign';
import type { CampaignCheckoutValues } from 'types/campaignCheckout';
import useStyles from './styles';
import { formatPrice, PaymentType, User } from '@elevator/ui';
import { canSubmitCheckout } from 'lib/checkoutValidation';

export type CampaignCheckoutActionsProps = {
  currentUser: User | null;
  handleBackClick: MouseEventHandler;
  paymentType: PaymentType;
  setPaypalOrderId: (value: string | null) => void;
  paypalOrderIdRef?: React.MutableRefObject<string | null>;
  totalCost: number;
  typeId: string;
};

const CampaignCheckoutActions = ({
  currentUser,
  handleBackClick,
  paymentType,
  setPaypalOrderId,
  paypalOrderIdRef,
  totalCost,
  typeId,
}: CampaignCheckoutActionsProps) => {
  const { classes } = useStyles();
  const apolloClient = useApolloClient();
  const isGuest = !currentUser;

  const { handleSubmit, isSubmitting, isValid, values } =
    useFormikContext<CampaignCheckoutValues>();

  const canSubmit = useMemo(
    () => canSubmitCheckout(paymentType, values, isValid),
    [paymentType, values, isValid],
  );

  const getCampaignTypeName = () => {
    const data = apolloClient.readQuery<{ campaignTypes: CampaignType[] }>({
      query: CAMPAIGN_TYPES,
    });
    const selectedType = data?.campaignTypes?.find((type) => type.typeId === typeId);
    return selectedType?.name ?? 'Campaign';
  };

  if (isGuest) {
    return (
      <ActionButtons
        cancel={{
          label: 'Back',
          onClick: handleBackClick,
        }}
      />
    );
  }

  return (
    <Box className={classes.actionButtonsRoot} data-component="CampaignCheckoutActions">
      <Box className={classes.actionButtonsWrap}>
        <ActionButtons
          submit={
            paymentType !== 'PayPal' && {
              label: 'Pay & Submit',
              onClick: () => handleSubmit(),
              disabled: !canSubmit || isSubmitting,
            }
          }
          cancel={{
            label: 'Back',
            onClick: handleBackClick,
            disabled: isSubmitting,
          }}
        />
      </Box>
      {paymentType === 'PayPal' && (
        <Slide in direction={'up'}>
          <Box className={classes.paypalSubmitButtonContainer}>
            <PayPalButton
              intent={'capture'}
              createOrder={(_data, actions) => {
                const priceString = formatPrice(totalCost);
                return actions.order.create({
                  intent: 'CAPTURE',
                  purchase_units: [
                    {
                      amount: {
                        currency_code: 'USD',
                        value: priceString,
                        breakdown: {
                          item_total: {
                            currency_code: 'USD',
                            value: priceString,
                          },
                        },
                      },
                      soft_descriptor: 'ELEVATOR LLC',
                      items: [
                        {
                          name: getCampaignTypeName(),
                          unit_amount: {
                            currency_code: 'USD',
                            value: priceString,
                          },
                          quantity: '1',
                        },
                      ],
                    },
                  ],
                });
              }}
              onApprove={async (data) => {
                if (paypalOrderIdRef) {
                  paypalOrderIdRef.current = data.orderID;
                }
                setPaypalOrderId(data.orderID);
                handleSubmit();
              }}
              disabled={isSubmitting}
            />
          </Box>
        </Slide>
      )}
    </Box>
  );
};

export default CampaignCheckoutActions;
